<script setup lang="ts">
import type { ApplicationSetup } from "@/stores"
import { DialogSize, KButton, KConfirmButton, KDialog } from "@kaleidoscope-solutions/ks-design-system"
import { ref } from "vue"
import Tabs from "@/components/Controls/Tabs.vue"
import ContentDropbox from "@/components/Setup/ContentDropbox.vue"
import SpinnerIcon from "@/components/Setup/SpinnerIcon.svg?component"

import { BaseSetupOption, OrgSetupOption, UserSetupOption, useSetupStore } from "@/stores"
import useAnalytics from "@/utils/analytics"
import { useSnackbar } from "@/utils/useSnackbar"

const { trackEvent } = useAnalytics()
const { addSnackbar } = useSnackbar()
const setupStore = useSetupStore()

const tabs = ref([
  { label: "User" },
  { label: "Organization" },
])

const isSaving = ref(false)
const currentTab = ref(0)

function closeDialog() {
  setupStore.setupDialog = false
}

// Default to first option
const selectedValues = ref<ApplicationSetup>({
  user: UserSetupOption.Nothing,
  org: OrgSetupOption.Nothing,
  base: BaseSetupOption.Nothing
})

// first tab form values
const userOptions = [
  { value: UserSetupOption.Nothing, label: "Do nothing (keep current)" },
  { value: UserSetupOption.Discard, label: "Discard personalization" },
  { value: UserSetupOption.Upload, label: "Upload custom" }
]
// second tab form values Organisation
const orgOptions = [
  { value: OrgSetupOption.Nothing, label: "Do nothing (keep current)" },
  { value: OrgSetupOption.ResetEmpty, label: "Reset to empty" },
  { value: OrgSetupOption.ResetDemo, label: "Reset to demo" },
  { value: OrgSetupOption.Upload, label: "Upload custom" }
]
const baseOptions = [
  { value: BaseSetupOption.Nothing, label: "Do nothing (keep current)" },
  { value: BaseSetupOption.Library, label: "Use library" },
  { value: BaseSetupOption.Upload, label: "Upload custom" }
]

async function handleSave() {
  if (isSaving.value) {
    return
  }

  isSaving.value = true
  try {
    await setupStore.applySettings(selectedValues.value)
    addSnackbar({
      type: "success",
      message: "Settings saved successfully.",
    })
    closeDialog()
    trackEvent("setup_saved")
  } catch (error: any) {
    addSnackbar({
      type: "error",
      message: "Failed to save settings.",
    })
    console.warn("error", error)
  } finally {
    isSaving.value = false
  }
}

function selectedTab(index: number) {
  currentTab.value = index
}

function uploadError(msg: string) {
  addSnackbar({ type: "error", message: msg })
}

function valid(settings: ApplicationSetup) {
  if (settings.base === BaseSetupOption.Upload && !settings.baseFiles) {
    return false
  }
  if (settings.user === UserSetupOption.Upload && !settings.userFiles) {
    return false
  }
  if (settings.org === OrgSetupOption.Upload && !settings.orgFiles) {
    return false
  }

  return true
}
</script>

<template>
  <KDialog
    open
    :size="DialogSize.fullsize"
    @close="closeDialog"
  >
    <template #default>
      <div class="flex size-full flex-col overflow-hidden">
        <h2 class="shrink-0 pb-6 font-vastago text-2xl font-bold">
          Application setup
        </h2>

        <div class="flex-1 overflow-auto">
          <Tabs
            :tabs="tabs"
            @change="selectedTab($event)"
          >
            <template #tab-0>
              <ContentDropbox
                title="Personalization"
                description="This includes personalization of widgets e.g. sort, layout, context filtering."
                :options="userOptions"
                :selected-value="selectedValues.user"
                name="user-radio-options"
                id-prefix="user"
                :upload-value="UserSetupOption.Upload"
                @update:selected-value="(val) => (selectedValues.user = val as UserSetupOption)"
                @upload-error="uploadError"
                @files-uploaded="(val) => (selectedValues.userFiles = val)"
              />
            </template>
            <template #tab-1>
              <div class="flex flex-col gap-4">
                <ContentDropbox
                  title="Configuration and data"
                  description="This includes org-specific configuration and all organization data for the application."
                  :options="orgOptions"
                  :selected-value="selectedValues.org"
                  name="org-radio-options"
                  id-prefix="org"
                  :upload-value="OrgSetupOption.Upload"
                  @update:selected-value="selectedValues.org = $event as OrgSetupOption"
                  @upload-error="uploadError"
                  @files-uploaded="(val) => (selectedValues.orgFiles = val)"
                />

                <ContentDropbox
                  title="Base"
                  description="This includes generic definitions for the application."
                  :options="baseOptions"
                  :selected-value="selectedValues.base"
                  name="base-radio-options"
                  id-prefix="base"
                  :upload-value="BaseSetupOption.Upload"
                  @update:selected-value="selectedValues.base = $event as BaseSetupOption"
                  @upload-error="uploadError"
                  @files-uploaded="(val) => (selectedValues.baseFiles = val)"
                />
              </div>
            </template>
          </Tabs>
        </div>

        <div class="flex shrink-0 items-center justify-end gap-2 p-2">
          <KButton
            variety="secondary"
            color="violet"
            @click="closeDialog"
          >
            Cancel
          </KButton>

          <template v-if="!isSaving">
            <KConfirmButton
              :disabled=" !valid(selectedValues)"
              color="red"
              @confirmed="handleSave"
            >
              {{ 'Hold to update application' }}
            </KConfirmButton>
          </template>

          <template v-else>
            <KButton
              color="red"
              class="cursor-not-allowed! hover:bg-red-700!"
            >
              <template #left>
                <SpinnerIcon />
              </template>
              Updating...
            </KButton>
          </template>
        </div>
      </div>
    </template>
  </KDialog>
</template>
